// Multi-workspace-tabs: intranet route paths gain an optional leading
// `/w/<org-slug>` prefix (MULTI_WORKSPACE_TABS.md §6a/§6g) so a browser tab can
// pin itself to one workspace. Only the workspaceProject intranet route set is
// prefixed — canvas (`/<owner>/<project>`, `/studio/*`), marketplace and preview
// are NEVER prefixed (§6b). `w` is RESERVED (added to app.js `_CANVAS_RESERVED`),
// so `/w/<x>` is UNAMBIGUOUSLY a workspace prefix, never a `<owner>/<key>` pair.
//
// AMENDED 2026-08-06 (per Nika "for canvas projects
// /w/<org>/<workspace>/p/<project>"): the canvas PROJECT page now has a
// workspace-scoped form — `/w/<org>/p/<key>` (HQ) and `/w/<org>/<ws>/p/<key>`
// — where the reserved `p` segment marks "the next segment is a project key".
// The residual route after org/ws parsing is `/p/<key>` (parse + compose live
// in ./projectUrls.js). Bare `/<owner>/<key>` stays valid forever (external
// links, cross-org projects). Other canvas surfaces (`/studio/*`) remain
// unprefixed.
//
// These are pure path-string helpers (guestRoutes.js pattern) — they take the
// path as an argument (Rule 64: reads are fine; navigation still goes through
// el.router) so callers pass either `window.location.pathname` or the
// router's `root.currentPath`.
//
// CORRECTION 2026-08-12 — this file's header USED to claim these are "NOT via
// el.call and NOT registered in functions/index.js", but functions/index.js
// DOES barrel `export * from './workspacePrefix.js'`, and
// `stripWorkspacePrefix` genuinely IS called via `el.call('stripWorkspacePrefix',
// …)` from tickets/Main.js + Sidebar.js — real, live registry usage, not just
// direct import. FA204 / frank trap applies: an exported function reading a
// module-scope const, OR a bare SIBLING export, is unresolvable once this
// file's exports are individually frank.toJSON'd for platform delivery
// (project_frank_drops_module_local_helpers; readerVoice.js precedent,
// workspace 8732aa07). Every export below is now self-contained, WITHOUT
// exception. The handle resolver — genuinely mutable cross-cutting state, SET
// once at boot by `registerWorkspaceHandleResolver` and READ by every parse
// call, with no `el`/`root` argument anywhere in this file's signatures to
// hoist state onto (unlike AppNavbar's `el.context` or navbarTabMenu's
// `root.__navbarSaveQueue`) — lives on `globalThis.__workspacePrefixState`,
// per the pointerCapability.js template. It used to be a module-scope `let`
// (`_wsHandleResolver`), a documented deliberate exception, until frank's
// emission gate started refusing it: see the Scheme A block below for the
// measurement. In practice this only affects the NOT-YET-SHIPPED two-segment
// `/w/<org>/<ws>/…` scheme (Phase 3); today, with zero live `/w/` URLs, every
// call falls through the one-segment/no-prefix branches, which never touch
// the resolver at all.
//
// CRITICAL PROPERTY — `stripWorkspacePrefix` is a NO-OP for every path that does
// NOT start with `/w/<slug>`. Until Phase 3 emits prefixed URLs, no path does,
// so wrapping a classifier in it is byte-identical today. That's why Phase 1
// (route-classifier hardening) ships safely on its own, ahead of the URL change.

// ── Scheme A (2026-07-30 per Nika): the workspace lives IN THE PATH ─────────
// `/w/<org>/<ws-handle>/<route…>`; the HQ keeps the concise `/w/<org>/<route…>`
// form. The ambiguity that originally forced the `?ws=` query param
// (`/w/icca/documents` — workspace or route?) is resolved DATA-DRIVEN: segment
// 2 reads as a workspace IFF the session knows a workspace of that org by that
// handle. The shell registers the resolver at boot
// (registerWorkspaceHandleResolver); before it is set — or when it says no —
// segment 2 reads as a route on the HQ, which is exactly the legacy
// interpretation, and the boot canonicalizer re-stamps once claims land.
// Server-side, new slugs are minted against a reserved blacklist mirroring the
// static route names, so fresh collisions cannot be created.
//
// The handle resolver is genuinely mutable cross-cutting state: set once here
// by `registerWorkspaceHandleResolver` and read by every parse call below,
// with no `el`/`root` argument in any of this file's signatures to hoist it
// onto instead. It USED to be a module-scope `let` (`_wsHandleResolver`), the
// ONE documented exception to this file's self-contained-export rule.
//
// FA201 / frank trap (WS-SHARED-MODULE-LET-BLOCKS-EVERY-PUSH-1) — that
// exception broke the publish path for EVERY project that inlines this
// package. frank hoists each export into the emitted `globalScope` and revives
// it with a bare indirect eval, where nothing module-level is in scope, so the
// three readers below each emitted a guaranteed ReferenceError on
// `_wsHandleResolver`. frank's emission gate refuses that (`PUBLISH BLOCKED —
// [unresolvable-identifier]`), which is why every push of a shared-inlining
// project needed `--no-verify-serialization`.
//
// Re-homed onto `globalThis.__workspacePrefixState`, per the
// pointerCapability.js template — the same always-reachable-global escape
// hatch that file's `window.__pointerCapabilityState` uses. `globalThis`
// rather than `window` because it is defined in every runtime this package is
// loaded in (browser, bun test, node), and in a browser it IS `window`, so the
// shell writes and the readers read one object. The lazy create is INLINED at
// each site on purpose: a shared module-scope getter would just relocate the
// same bare-sibling-call hazard this fix removes.
export const registerWorkspaceHandleResolver = (fn) => {
  if (!globalThis.__workspacePrefixState) {
    globalThis.__workspacePrefixState = { handleResolver: null }
  }
  globalThis.__workspacePrefixState.handleResolver = typeof fn === 'function' ? fn : null
}

// One parse for the whole `/w/…` shape → { orgSlug, wsHandle, route }.
// `wsHandle` is non-null only when the registered resolver recognizes segment
// 2 as a workspace of that org; `route` is always the underlying app route
// ('/' when the path is just the prefix).
export const parseWorkspacePath = (pathname) => {
  // Org slug per MODEL: required, globally-unique, [a-z0-9-]{1,48}. The
  // lookahead admits `?`/`#` too so a composed path+query (`/w/acme?x=1`,
  // planMwtNav's inputs) parses the same as a bare pathname.
  const WORKSPACE_PREFIX_RE = /^\/w\/[a-z0-9-]{1,48}(?=[/?#]|$)/
  const WORKSPACE_SLUG_RE = /^\/w\/([a-z0-9-]{1,48})(?=[/?#]|$)/
  // Segment 2 — WHICH workspace. Handles are workspace slugs OR keys
  // (`ws_jfTumkSWof`) or raw ids, hence the wider charset than org slugs.
  const WORKSPACE_TWO_SEG_RE = /^\/w\/([a-z0-9-]{1,48})\/([A-Za-z0-9_-]{1,64})(?=[/?#]|$)/
  // Segment-2 names that can NEVER be a workspace handle — static route
  // markers under `/w/<org>/…`, gated here so not even a lying/stale
  // resolver can claim them. `p` is the canvas-project marker
  // (`/w/<org>/p/<key>` — the HQ form puts it in segment 2). Mirrored
  // server-side in RESERVED_WORKSPACE_SLUGS (server
  // src/core/utils/workspaceSlug.js) so no workspace can mint the slug.
  const RESERVED_WS_SEG2 = new Set(['p'])
  const isWsHandle = (orgSlug, seg) => {
    if (!seg || RESERVED_WS_SEG2.has(seg)) return false
    const resolver = globalThis.__workspacePrefixState?.handleResolver
    if (!resolver) return false
    try {
      return !!resolver(orgSlug, seg)
    } catch {
      return false
    }
  }
  const restToRoute = (rest) => {
    if (rest === '') return '/'
    // A prefix followed directly by query/hash (`/w/acme?x=1`) strips to
    // `?x=1` — normalize to a rooted route so classifiers always see a path.
    return rest.startsWith('?') || rest.startsWith('#') ? '/' + rest : rest
  }
  // `default` is NEVER a route. It is the HQ slug convention (server
  // workspaceSlug.js: 'Default' → slug 'default', left unreserved because it is
  // unambiguous as segment 2), so `/w/<org>/default/…` links reach orgs whose
  // default workspace carries another slug (`hq`) or none (key only). Where a
  // route would start it is dropped — never read as the `/default` route
  // ("Not found"): at segment 2 when no workspace of the org claims it (the
  // org's default landing, exactly the bare `/w/<org>`), and right after a
  // handle, where only a double stamp (`/w/<org>/<handle>/default`) puts it.
  const dropDefault = (rest) => (/^\/default(?=[/?#]|$)/i.test(rest) ? rest.slice(8) : rest)

  if (typeof pathname !== 'string' || !pathname) {
    return { orgSlug: null, wsHandle: null, route: pathname || '/' }
  }
  const two = pathname.match(WORKSPACE_TWO_SEG_RE)
  if (two && isWsHandle(two[1], two[2])) {
    return {
      orgSlug: two[1],
      wsHandle: two[2],
      route: restToRoute(dropDefault(pathname.slice(two[0].length)))
    }
  }
  const one = pathname.match(WORKSPACE_SLUG_RE)
  if (one) {
    return {
      orgSlug: one[1],
      wsHandle: null,
      route: restToRoute(dropDefault(pathname.replace(WORKSPACE_PREFIX_RE, '')))
    }
  }
  return { orgSlug: null, wsHandle: null, route: pathname }
}

// The path's workspace handle (segment 2), or null. Resolver-gated like
// parseWorkspacePath — a self-contained duplicate of the same logic (a bare
// sibling-export call is exactly as unresolvable post-frank.toJSON as a
// module-local read; see the file-header CORRECTION comment).
export const getWorkspacePathHandle = (pathname) => {
  const WORKSPACE_TWO_SEG_RE = /^\/w\/([a-z0-9-]{1,48})\/([A-Za-z0-9_-]{1,64})(?=[/?#]|$)/
  const RESERVED_WS_SEG2 = new Set(['p'])
  const isWsHandle = (orgSlug, seg) => {
    if (!seg || RESERVED_WS_SEG2.has(seg)) return false
    const resolver = globalThis.__workspacePrefixState?.handleResolver
    if (!resolver) return false
    try {
      return !!resolver(orgSlug, seg)
    } catch {
      return false
    }
  }
  if (typeof pathname !== 'string' || !pathname) return null
  const two = pathname.match(WORKSPACE_TWO_SEG_RE)
  return two && isWsHandle(two[1], two[2]) ? two[2] : null
}

// RAW segments, NO resolver gating — `{ orgSlug, seg2 }` where `seg2` is
// whatever sits in position 2 (workspace handle OR first route segment,
// undecided). For callers that hold the org's workspace list themselves
// (resolveActiveWorkspace at boot, before the shell resolver registers) and
// can therefore make the workspace-or-route call directly against data.
export const getWorkspaceRawSegments = (pathname) => {
  const WORKSPACE_SLUG_RE = /^\/w\/([a-z0-9-]{1,48})(?=[/?#]|$)/
  const WORKSPACE_TWO_SEG_RE = /^\/w\/([a-z0-9-]{1,48})\/([A-Za-z0-9_-]{1,64})(?=[/?#]|$)/
  if (typeof pathname !== 'string' || !pathname) return { orgSlug: null, seg2: null }
  const two = pathname.match(WORKSPACE_TWO_SEG_RE)
  if (two) return { orgSlug: two[1], seg2: two[2] }
  const one = pathname.match(WORKSPACE_SLUG_RE)
  return { orgSlug: one ? one[1] : null, seg2: null }
}

// `/w/acme/tickets` → `/tickets`   `/w/acme` → `/`   `/tickets` → `/tickets`
// `/w/acme/team-b/tickets` → `/tickets` once the resolver knows `team-b`.
// Self-contained duplicate of parseWorkspacePath's logic (a bare
// sibling-export call is exactly as unresolvable post-frank.toJSON as a
// module-local read) — this is the export genuinely called live via
// `el.call('stripWorkspacePrefix', …)` (tickets/Main.js + Sidebar.js), so
// this one is not just theoretical.
export const stripWorkspacePrefix = (pathname) => {
  const WORKSPACE_PREFIX_RE = /^\/w\/[a-z0-9-]{1,48}(?=[/?#]|$)/
  const WORKSPACE_SLUG_RE = /^\/w\/([a-z0-9-]{1,48})(?=[/?#]|$)/
  const WORKSPACE_TWO_SEG_RE = /^\/w\/([a-z0-9-]{1,48})\/([A-Za-z0-9_-]{1,64})(?=[/?#]|$)/
  const RESERVED_WS_SEG2 = new Set(['p'])
  const isWsHandle = (orgSlug, seg) => {
    if (!seg || RESERVED_WS_SEG2.has(seg)) return false
    const resolver = globalThis.__workspacePrefixState?.handleResolver
    if (!resolver) return false
    try {
      return !!resolver(orgSlug, seg)
    } catch {
      return false
    }
  }
  const restToRoute = (rest) => {
    if (rest === '') return '/'
    return rest.startsWith('?') || rest.startsWith('#') ? '/' + rest : rest
  }
  // `default` is never a route — see parseWorkspacePath's dropDefault.
  const dropDefault = (rest) => (/^\/default(?=[/?#]|$)/i.test(rest) ? rest.slice(8) : rest)
  if (typeof pathname !== 'string' || !pathname) return pathname
  const two = pathname.match(WORKSPACE_TWO_SEG_RE)
  if (two && isWsHandle(two[1], two[2])) {
    return restToRoute(dropDefault(pathname.slice(two[0].length)))
  }
  const one = pathname.match(WORKSPACE_SLUG_RE)
  if (one) return restToRoute(dropDefault(pathname.replace(WORKSPACE_PREFIX_RE, '')))
  return pathname
}

// `/tickets/board` → `/tasks/board`   `/ticket/abc` → `/task/abc`
// `/tasks` → `/tasks` (already canonical)   `/chat` → `/chat` (no alias).
// Expects an already workspace-prefix-STRIPPED path.
// Every shape a caller may legitimately match a declared APP route against:
// the raw path, then the same path with a `/w/<org>` tenancy prefix removed,
// then with `/w/<org>/<ws-handle>` removed. Longest-lived caller: bootShell's
// i18n namespace pick, which tests a manifest's `routes` (written against the
// APP path — `/^\/(tickets|chat|…)/`) against the BROWSER path.
//
// Why candidates and not `stripWorkspacePrefix`: that one needs the handle
// resolver to decide whether segment 2 is a workspace handle, and at boot the
// resolver is not registered yet — it returned `/ws_EEYlHGTvZ8/tickets`, which
// matches nothing. A caller here matches each candidate in turn and uses the
// first that HITS a route it declared, so a stripped candidate can never eat a
// real first segment: `/w/acme/tickets` still matches on the one-segment form.
//
// MOST SPECIFIC FIRST, and the caller must take the FIRST candidate that
// matches, never the union. Segment 2 is ambiguous without the resolver —
// `/w/acme/tickets` is an org prefix + the `/tickets` route, but has the same
// shape as an org + handle — so both readings are offered and specificity
// decides: `/tickets` is tried before the `/` that the handle reading implies.
//
// `/w/acme/ws_1/tickets` → ['/w/acme/ws_1/tickets', '/ws_1/tickets', '/tickets']
// `/w/acme/tickets`      → ['/w/acme/tickets', '/tickets', '/']
// `/tickets`             → ['/tickets']
export const routePathCandidates = (pathname) => {
  const ORG_RE = /^\/w\/[a-z0-9-]{1,48}(?=[/?#]|$)/
  const TWO_SEG_RE = /^\/w\/[a-z0-9-]{1,48}\/[A-Za-z0-9_-]{1,64}(?=[/?#]|$)/
  const path = typeof pathname === 'string' && pathname ? pathname : '/'
  const out = [path]
  const push = (rest) => {
    const next = rest === '' ? '/' : rest
    if (out.indexOf(next) === -1) out.push(next)
  }
  const org = path.match(ORG_RE)
  if (!org) return out
  push(path.slice(org[0].length))
  const two = path.match(TWO_SEG_RE)
  if (two) push(path.slice(two[0].length))
  return out
}

export const canonicalRoutePath = (pathname) => {
  // Legacy → canonical route-prefix aliases. The tickets feature renamed its
  // USER-FACING route to /tasks (2026-08-06 per Nika "instead of /tickets it
  // will be /tasks") while the old /tickets* URLs stay registered as inbound
  // aliases in the shell route map — so active-state matchers (AppNavbar,
  // HeaderTabs) normalize a live path through canonicalRoutePath before
  // comparing against an item's canonical route. Longest prefix first. The
  // DATA plane (sdk.tickets.*, root.tickets) keeps its internal names.
  const LEGACY_ROUTE_ALIASES = [
    // Requests/Resolutions moved OUT of the tickets sidebar to their own
    // top-level surface before the rename — their legacy forms canonicalize
    // there, not under /tasks (a /tasks/requests route does not exist).
    ['/tickets/requests/audit', '/resolutions/audit'],
    ['/tickets/requests', '/resolutions/requests'],
    ['/tickets/resolutions', '/resolutions'],
    ['/tickets-page', '/tasks-page'],
    ['/tickets', '/tasks'],
    ['/ticket', '/task'],
    // Documents renamed to Notes (2026-08-06 per Nika). The old three-way
    // section split collapsed: /documents/notes IS the main list now, and
    // /documents/kb joins the canonical /notes/knowledge-base form.
    ['/documents/knowledge-base', '/notes/knowledge-base'],
    ['/documents/kb', '/notes/knowledge-base'],
    ['/documents/notes', '/notes'],
    ['/documents/editor', '/notes/editor'],
    ['/documents', '/notes'],
    ['/docs', '/notes']
  ]
  const p = typeof pathname === 'string' ? pathname : ''
  const hit = LEGACY_ROUTE_ALIASES.find(([from]) => p === from || p.startsWith(from + '/'))
  return hit ? hit[1] + p.slice(hit[0].length) : p
}

// The org-slug carried by the prefix, or null when the path has none.
export const getWorkspacePrefixSlug = (pathname) => {
  const WORKSPACE_SLUG_RE = /^\/w\/([a-z0-9-]{1,48})(?=[/?#]|$)/
  if (typeof pathname !== 'string') return null
  const m = pathname.match(WORKSPACE_SLUG_RE)
  return m ? m[1] : null
}

// Self-contained duplicate of getWorkspacePrefixSlug's check rather than a
// bare sibling-export call.
export const hasWorkspacePrefix = (pathname) => {
  const WORKSPACE_SLUG_RE = /^\/w\/([a-z0-9-]{1,48})(?=[/?#]|$)/
  if (typeof pathname !== 'string') return false
  return WORKSPACE_SLUG_RE.test(pathname)
}

// Compose a prefixed intranet path from a slug + an underlying route (Phase 3+
// switcher/router use). `('acme', '/tickets')` → `/w/acme/tickets`;
// `('acme', '/')` → `/w/acme`. A falsy slug returns the bare path (no prefix).
export const withWorkspacePrefix = (slug, pathname = '/') => {
  const base = typeof pathname === 'string' && pathname ? pathname : '/'
  if (!slug) return base
  const rest = base === '/' ? '' : base.startsWith('/') ? base : `/${base}`
  return `/w/${slug}${rest}`
}

// Compose the FULL scheme-A path: org + optional workspace handle + route.
// `('acme', 'team-b', '/tickets')` → `/w/acme/team-b/tickets`;
// `('acme', null, '/tickets')` → `/w/acme/tickets` (the HQ form).
export const withWorkspacePath = (orgSlug, wsHandle, pathname = '/') => {
  const base = typeof pathname === 'string' && pathname ? pathname : '/'
  if (!orgSlug) return base
  const rest =
    base === '/'
      ? ''
      : // `/?x=1` (home + query) joins as `?x=1`, not `/?x=1`
        (base.startsWith('/') ? base : `/${base}`).replace(/^\/(?=[?#])/, '')
  return wsHandle ? `/w/${orgSlug}/${wsHandle}${rest}` : `/w/${orgSlug}${rest}`
}

// ── Which workspace, not just which org ─────────────────────────────────────
//
// `/w/<org-slug>` names the ORGANISATION. An org with more than one workspace
// therefore has NO addressable URL for a specific one: boot resolves the prefix
// to the org's HQ workspace, so a two-workspace org always snaps back to HQ on
// reload, a shared link never opens the workspace the sender was looking at,
// and two tabs cannot sit in different workspaces of the same org.
//
// The workspace is carried as a QUERY param rather than a second path segment
// deliberately. `/w/<org>/<workspace>/<route>` cannot be parsed without first
// knowing the org's workspace list — `/w/icca/documents` is ambiguous between
// a workspace named "documents" and the documents route. A query param has no
// such collision, and it is additive: every existing URL keeps working
// unchanged, which matters because the prefix scheme is already live.
//
// The value is a workspace SLUG (falling back to an id) — slugs are unique
// within an org, which is the only scope this is ever resolved in. The key
// name ('ws') is duplicated as a local literal in both functions below
// rather than a shared module-scope const (FA204 / frank trap).

// Read the workspace handle out of either a bare query string
// (`'?ws=certification'`) or a whole path+query (`'/w/icca/exams?ws=cert'`).
// Accepting both matters: callers hold `location.search` in one place and a
// composed URL in another, and feeding a full URL to a search-only parser fails
// SILENTLY (`URLSearchParams` reads `/w/icca/exams?ws` as the key) — which read
// as "no workspace named" and quietly dropped the handle.
export const getWorkspaceQueryParam = (search) => {
  const WORKSPACE_QUERY_KEY = 'ws'
  if (typeof search !== 'string' || !search) return null
  const qAt = search.indexOf('?')
  const qs = qAt === -1 ? search : search.slice(qAt + 1)
  try {
    const v = new URLSearchParams(qs.split('#')[0]).get(WORKSPACE_QUERY_KEY)
    return v ? String(v).trim() || null : null
  } catch {
    return null
  }
}

// Add/replace `?ws=<slug>` on a path+query string, preserving every other param.
// A falsy slug REMOVES the param, so "back to the org default" is expressible.
export const withWorkspaceQuery = (url, slug) => {
  const WORKSPACE_QUERY_KEY = 'ws'
  const raw = typeof url === 'string' && url ? url : '/'
  const hashAt = raw.indexOf('#')
  const hash = hashAt === -1 ? '' : raw.slice(hashAt)
  const noHash = hashAt === -1 ? raw : raw.slice(0, hashAt)
  const qAt = noHash.indexOf('?')
  const path = qAt === -1 ? noHash : noHash.slice(0, qAt)
  const params = new URLSearchParams(qAt === -1 ? '' : noHash.slice(qAt))
  if (slug) params.set(WORKSPACE_QUERY_KEY, String(slug))
  else params.delete(WORKSPACE_QUERY_KEY)
  const q = params.toString()
  return path + (q ? '?' + q : '') + hash
}
